import { useEffect, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { getQueryKey, queryCompanyViews, mutateCompanyViews } from './queries';
import { format } from 'date-fns';
import { useUserAccess } from '@/hooks/useUserAccess';
import useUser from '../useUser';
import { useDateFormatter } from '@/hooks/useDateFormatter';

export function useCompanyLimits(companyId?: string) {
  const [visited, setVisited] = useState(false);
  const { formatDistanceToNow } = useDateFormatter();
  const { data: userData } = useUser();
  const { status, data } = useQuery({
    queryKey: getQueryKey(userData.id),
    queryFn: queryCompanyViews,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
    enabled: userData.loggedIn,
    select: (response) => {
      if (typeof response === 'undefined') {
        return { companies: [], views: 0, limit: Infinity, rewards: [] };
      }
      return {
        companies: response.data.map((company) => {
          return {
            id: company.company_id.toLowerCase(),
            name: company.name,
            expiryTime: formatDistanceToNow(company.expiry_time),
            uniqueSymbol: company.unique_symbol,
            canonicalUrl: company.canonical_url,
          };
        }),
        views: response.meta.total,
        limit: response.meta.limit,
        restricted: response.meta.plan_restricted,
        resetDate: format(response.meta.reset_date || 0, 'dd/MM/yyyy'),
        rewards: response.rewards.map((company) => {
          return {
            id: company.company_id.toLowerCase(),
            name: company.name,
            expiryTime: formatDistanceToNow(company.expiry_time),
            uniqueSymbol: company.unique_symbol,
            canonicalUrl: company.canonical_url,
          };
        }),
      };
    },
  });

  useEffect(() => {
    if (companyId !== undefined) {
      const visited =
        [...(data?.companies || []), ...(data?.rewards || [])].findIndex(
          //TODO @ric: once company ID responses from API are all in lower case, remove this toLowerCase()
          //ref: https://gitlab.com/simplywallst/frontend/mono/-/merge_requests/4477
          (company) => company.id === companyId.toLowerCase()
        ) !== -1;
      setVisited(visited);
    }
  }, [data, companyId]);

  return {
    status,
    companies: data?.companies || [],
    rewards: data?.rewards || [],
    views: data?.views || 0,
    limit: data?.limit || 0,
    resetDate: data?.resetDate || 0,
    restricted: data?.restricted || false,
    visited,
  };
}

export function useUpdateCompanyLimits(companyId?: string) {
  const [restricted, setRestricted] = useState(false);
  const [status, setStatus] = useState<
    'idle' | 'error' | 'loading' | 'success'
  >('idle');
  const uac = useUserAccess();
  const {
    companies,
    status: companyLimitsStatus,
    restricted: companyRestricted,
  } = useCompanyLimits();
  const client = useQueryClient();
  const { data: userData } = useUser();

  const {
    status: limitsStatus,
    data: limitsData,
    mutate,
  } = useMutation({
    mutationFn: mutateCompanyViews,
    onSuccess(response) {
      client.setQueryData(getQueryKey(userData.id), response);
    },
  });

  useEffect(() => {
    if (
      uac.status === 'success' &&
      companyLimitsStatus === 'success' &&
      companyId !== undefined
    ) {
      if (typeof uac.data !== 'undefined') {
        const validCompany =
          companies.findIndex(
            (company) => company.id.toLowerCase() === companyId.toLowerCase()
          ) !== -1;

        if (validCompany) {
          /** current company is in users valid list */
          setRestricted(false);
          setStatus('success');
        } else if (companyRestricted) {
          /** maxed out company limit */
          setRestricted(true);
          setStatus('success');
          //Only update company limit if user has a plan (i.e. logged in)
        } else if (limitsStatus === 'idle' && uac.data.planType) {
          /**  */
          mutate(companyId);
        }
      }
    }
  }, [
    companyRestricted,
    uac.status,
    uac.data,
    status,
    limitsStatus,
    mutate,
    companies,
    companyLimitsStatus,
    companyId,
  ]);

  const planRestricted = limitsData?.meta.plan_restricted;

  useEffect(() => {
    const needsInvalidation =
      companies.findIndex(
        (company) => company.id.toLowerCase() === companyId?.toLowerCase()
      ) === -1;

    if (
      limitsStatus === 'success' &&
      typeof planRestricted !== 'undefined' &&
      needsInvalidation
    ) {
      setStatus('success');
      setRestricted(planRestricted);
    }
  }, [limitsStatus, planRestricted, companies, companyId, status]);

  return {
    status,
    restricted,
  };
}

export function useGetMutateCompanyLimit() {
  const client = useQueryClient();
  const { data: userData } = useUser();
  const { mutate: mutateCompanyView, status: mutateCompanyViewStatus } =
    useMutation({
      mutationFn: mutateCompanyViews,
      onSuccess(data) {
        client.setQueryData(getQueryKey(userData.id), data);
      },
    });

  return { mutateCompanyView, mutateCompanyViewStatus };
}
